import { useEffect, useId, useRef, useState } from 'react'
import PlateHeader from './m1nt/PlateHeader'
import Holo3DCard from './m1nt/card/Holo3DCard'
import AuthenticationSeal from './m1nt/card/AuthenticationSeal'
import CardSealBadge from './m1nt/card/CardSealBadge'
import CardAuthLegend from './m1nt/card/CardAuthLegend'
import MediaLightbox from './m1nt/card/MediaLightbox'
import { getChainName } from './m1nt/card/cardFields'
import { getFlowConfig } from '../utils/cardFlowConfig'

const VIEW_CHAIN_ID = parseInt(import.meta.env.VITE_CHAIN_ID || '80002', 10)

export default function CardTimeline({ card, order, steps, imageUrl, onBack, onOpenDetails }) {
  const activeRef = useRef(null)
  const [viewerOpen, setViewerOpen] = useState(false)
  const [expanded, setExpanded] = useState(false)
  const listId = useId()
  const cardImageUrl = imageUrl || card?.frontImageUrl || '/placeholder-card.png'
  const tokenId = order?.tokenId ?? card?.tokenId ?? null
  const cfg = getFlowConfig(order?.status || 'PENDING_QUOTE')
  // "Action needed" cards wait on the owner; "In progress" cards wait on the lab or the carrier.
  const ownerActs = Boolean(cfg?.ownerAction)

  // Collapsed, the journey shows where the card is: the step before, the active step (the one to
  // click) and the next one. With no active step it shows the three most recent ones.
  const activeIndex = steps.findIndex((step) => step.status === 'active')
  const lastDoneIndex = steps.reduce(
    (found, step, i) => (step.status === 'done' || step.status === 'completed' ? i : found),
    -1
  )
  const focusEnd =
    activeIndex >= 0 ? Math.min(steps.length - 1, activeIndex + 1) : Math.max(lastDoneIndex, 0)
  const focusStart = activeIndex >= 0 ? Math.max(0, activeIndex - 1) : Math.max(0, focusEnd - 2)
  const hiddenCount = steps.length - (focusEnd - focusStart + 1)
  const visibleSteps = steps
    .map((step, index) => ({ step, index }))
    .filter(
      ({ index }) => expanded || hiddenCount <= 0 || (index >= focusStart && index <= focusEnd)
    )

  useEffect(() => {
    const el = activeRef.current
    if (!el) return undefined
    const t = setTimeout(() => {
      el.scrollIntoView({ behavior: 'smooth', block: 'center' })
    }, 400)
    return () => clearTimeout(t)
  }, [steps])

  return (
    <div className="flex flex-col">
      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <button
          type="button"
          onClick={onBack}
          className="inline-flex min-h-[44px] items-center gap-2 self-start text-[14px] tracking-[0.04em] text-m1-ink transition-colors hover:text-m1-violet"
        >
          <i className="ri-arrow-left-line text-lg" aria-hidden="true" />
          Back to collection
        </button>
        {onOpenDetails && (
          <button
            type="button"
            onClick={onOpenDetails}
            className="inline-flex min-h-[44px] items-center justify-center border border-white/30 px-5 text-[14px] tracking-[0.04em] text-m1-ink transition-colors hover:border-m1-violet hover:text-m1-violet"
          >
            Details &amp; actions
          </button>
        )}
      </div>

      <div className="m1nt-plate mt-6 flex flex-col gap-6 p-5 sm:flex-row sm:items-center sm:p-7">
        <div className="w-[150px] flex-shrink-0 self-center sm:w-[190px] sm:self-start">
          <Holo3DCard
            imageUrl={cardImageUrl}
            fallbackImageUrl="/placeholder-card.png"
            alt={card?.name || 'Card'}
            showAfterSplash
            onActivate={() => setViewerOpen(true)}
          />
        </div>
        <div className="min-w-0 flex-1">
          <h3 className="font-m1-display text-[clamp(26px,3vw,36px)] font-normal leading-tight tracking-[-0.01em] text-m1-ink">
            {card?.name}
          </h3>
          <p className="mt-2 text-[16px] font-light text-m1-muted">{card?.cardType || '—'}</p>
          <p className="mt-4 inline-flex items-center gap-2 font-m1-mono text-[11px] uppercase tracking-[0.12em] text-m1-violet">
            <span
              className="card-journey-dot-pulse h-1.5 w-1.5 rounded-full bg-m1-violet"
              aria-hidden="true"
            />
            {cfg.label}
          </p>
          {tokenId != null && (
            <div className="mt-6 max-w-[380px]">
              <AuthenticationSeal
                tokenId={tokenId}
                chainId={VIEW_CHAIN_ID}
                chainName={getChainName(VIEW_CHAIN_ID)}
                onActivate={onOpenDetails}
              />
            </div>
          )}
        </div>
      </div>

      <div className="m1nt-plate mt-8 p-5 sm:p-7">
        <PlateHeader
          icon={<i className="ri-links-line" />}
          eyebrow="Every handover, in order"
          title="Chain of custody"
        />

        <ol id={listId} className="relative mt-6">
          <span
            className="absolute bottom-3 left-[13px] top-3 w-px bg-m1-line"
            aria-hidden="true"
          />
          {visibleSteps.map(({ step, index }, offset) => {
            const isDone = step.status === 'done' || step.status === 'completed'
            const isActive = step.status === 'active'
            const clickable = isActive && Boolean(onOpenDetails)

            const body = (
              <>
                <div className="flex flex-wrap items-center gap-x-3 gap-y-1">
                  <span
                    className={`text-[18px] ${isActive ? 'font-normal text-m1-ink' : 'font-light text-m1-ink-soft'}`}
                  >
                    {step.stepName}
                  </span>
                  <span
                    className={`font-m1-mono text-[11px] uppercase tracking-[0.12em] ${
                      isDone ? 'text-m1-ice' : isActive ? 'text-m1-violet' : 'text-m1-muted'
                    }`}
                  >
                    {isDone
                      ? 'Done'
                      : isActive
                        ? ownerActs
                          ? 'Your turn'
                          : 'In progress'
                        : 'Pending'}
                  </span>
                </div>
                <p className="mt-1 text-[14px] text-m1-muted">{step.date || '—'}</p>
                {step.txHash && (
                  <div className="mt-3 space-y-1 break-all text-[13px]">
                    <p className="text-m1-muted">
                      TX: <span className="font-m1-mono text-m1-ink-soft">{step.txHash}</span>
                    </p>
                    {step.blockNumber && (
                      <p className="text-m1-muted">
                        Block:{' '}
                        <span className="font-m1-mono text-m1-ink-soft">{step.blockNumber}</span>
                      </p>
                    )}
                    <p className="inline-flex items-center gap-1.5 font-m1-mono text-[11px] uppercase tracking-[0.12em] text-m1-ice">
                      <i className="ri-shield-check-line text-sm" aria-hidden="true" />
                      On-chain record
                    </p>
                  </div>
                )}
              </>
            )

            return (
              <li
                key={step.stepKey || index}
                ref={isActive ? activeRef : undefined}
                className="card-journey-step-in relative flex gap-5 pb-5 last:pb-0"
                style={{ animationDelay: `${offset * 80}ms` }}
              >
                <span
                  className={`relative z-[1] mt-1 flex h-7 w-7 flex-shrink-0 items-center justify-center rounded-full border bg-m1-ground text-[12px] ${
                    isDone
                      ? 'border-m1-ice text-m1-ice'
                      : isActive
                        ? 'border-m1-violet text-m1-violet'
                        : 'border-m1-line text-m1-muted'
                  }`}
                  aria-hidden="true"
                >
                  {isActive && clickable && (
                    <span className="absolute inset-0 rounded-full border border-m1-violet motion-safe:animate-ping" />
                  )}
                  {isDone ? <i className="ri-check-line text-sm" /> : <span>{index + 1}</span>}
                </span>

                {clickable ? (
                  <button
                    type="button"
                    onClick={onOpenDetails}
                    aria-label={`${step.stepName}: open details and actions`}
                    className="group flex min-w-0 flex-1 items-center justify-between gap-4 rounded-[12px] border border-m1-violet/60 bg-m1-violet/10 px-4 py-4 text-left transition-colors hover:border-white hover:bg-white/[0.06] focus:outline-none focus-visible:border-white"
                  >
                    <span className="min-w-0 flex-1">{body}</span>
                    <span
                      className={`inline-flex shrink-0 items-center gap-2 border px-3.5 py-2 text-[13px] font-medium transition-colors ${
                        ownerActs
                          ? 'border-white bg-white text-m1-ground group-hover:border-[#e4e4e4] group-hover:bg-[#e4e4e4]'
                          : 'border-white/40 text-m1-ink group-hover:border-white group-hover:bg-white group-hover:text-m1-ground'
                      }`}
                    >
                      {ownerActs ? 'Take action' : 'View status'}
                      <i
                        className="ri-arrow-right-line text-base transition-transform group-hover:translate-x-0.5"
                        aria-hidden="true"
                      />
                    </span>
                  </button>
                ) : (
                  <div
                    className={`min-w-0 flex-1 rounded-[12px] border px-4 ${
                      isActive
                        ? 'border-m1-violet/60 bg-m1-violet/10 py-3'
                        : 'border-m1-line-soft bg-black/20 py-2.5'
                    }`}
                  >
                    {body}
                  </div>
                )}
              </li>
            )
          })}
        </ol>

        {hiddenCount > 0 && (
          <button
            type="button"
            onClick={() => setExpanded((value) => !value)}
            aria-expanded={expanded}
            aria-controls={listId}
            className="mt-5 inline-flex min-h-[40px] items-center gap-1.5 text-[14px] text-white/80 transition-colors hover:text-white"
          >
            {expanded ? 'Show fewer steps' : `Show all ${steps.length} steps`}
            <i
              className={`ri-arrow-down-s-line text-lg leading-none transition-transform ${
                expanded ? 'rotate-180' : ''
              }`}
              aria-hidden="true"
            />
          </button>
        )}
      </div>

      <MediaLightbox
        media={viewerOpen ? { kind: 'image', url: cardImageUrl, alt: card?.name || 'Card' } : null}
        onClose={() => setViewerOpen(false)}
        badge={tokenId != null ? <CardSealBadge /> : undefined}
        legend={tokenId != null ? <CardAuthLegend /> : undefined}
        immersive
        tilt
      />
    </div>
  )
}
